/* eslint-disable */

export default class VerticalGrid {
  constructor(userConfig) {
    // load options
    this._loadConfig(userConfig);

    // listen for resize
    if (this._config.listenForResize) this._addResizeListener();
  }

  layoutGrid(gridEl) {
    // layout grid
    this._layoutGrid(gridEl);
  }

  makeAllGrids() {
    // find all grids
    const allGrids = this._getAllGrids();

    // loop over grids
    allGrids.forEach(gridEl => {
      this._layoutGrid(gridEl);
    });
  }

  updateConfig(userConfig) {
    this._updateConfig(userConfig);
  }

  resetConfig(userConfig) {
    this._loadConfig(userConfig);
  }

  _gridReady = false;
  _containerWidth = 0;
  _calculatedGutter = 0;
  _columnHeights = [];
  _columnWidth = 0;
  _columnPositions = [];

  _defaultConfig = {
    containerClass: '.vertical-grid',
    itemClass: '.item',
    widthAttribute: 'w',
    heightAttribute: 'h',
    columns: 5,
    horizontalGutter: 10,
    verticalGutter: 10,
    equalizeColumnHeights: true,
    skipFirstRow: true,
    setShortestHeight: true,
    listenForResize: true,
  };
  _config = { ...this._defaultConfig };

  _loadConfig(userConfig) {
    let config = userConfig
      ? { ...this._defaultConfig, ...userConfig }
      : { ...this._defaultConfig };

    this._config = { ...config };
  }

  _updateConfig(userConfig) {
    const config = userConfig
      ? { ...this._config, ...userConfig }
      : { ...this._config };

    this._config = { ...config };
  }

  _addResizeListener() {
    window.addEventListener('resize', () => {
      if (this._gridReady) this.makeAllGrids();
    });
  }

  _getAllGrids() {
    const allGrids = document.querySelectorAll(
      this._config.containerClass,
    );
    return allGrids;
  }

  _layoutGrid(gridEl) {
    // - setup column height array
    this._setupColumnHeights();

    // - set container width
    this._containerWidth = gridEl.offsetWidth;

    // - calculate column widths
    this._calculateColumnWidth();

    // - calculate column positions
    this._calculateColumnPositions();

    // - find all grid items
    const allGridItems = this._getAllGridItems(gridEl);

    // - loop over grid items in batches
    this._batchGridItems([...allGridItems], (gridItemBatch, index) => {
      this._processRow(gridItemBatch, index);
    });

    // - set final grid height
    const sortedHeights = this._columnHeights.sort(
      this._sortDescending,
    );
    gridEl.style.height = `${sortedHeights[0] -
      this._config.verticalGutter}px`;

    // - set shortest grid height of shortest item
    if (this._config.setShortestHeight) {
      const shortestPx = `${sortedHeights[sortedHeights.length - 1] - this._config.verticalGutter}px`;
      gridEl.style.setProperty("--shortest", shortestPx);
    }
        
    // - mark grid as ready
    this._gridReady = true;
    gridEl.dataset.gridReady = this._gridReady;
  }

  _setupColumnHeights() {
    const columnHeights = [];
    for (let i = 0; i < this._config.columns; i++) {
      columnHeights.push(0);
    }

    this._columnHeights = columnHeights;
  }

  _calculateColumnWidth() {
    const numberOfGutters = this._config.columns - 1;

    const totalGutterSpace =
      numberOfGutters * this._config.horizontalGutter;

    const availableItemSpace =
      this._containerWidth - totalGutterSpace;

    const columnWidth = availableItemSpace / this._config.columns;

    this._columnWidth = columnWidth;
  }

  _calculateColumnPositions() {
    const columnPositions = [];

    for (let i = 0; i < this._config.columns; i++) {
      const gutterOffset = this._config.horizontalGutter * i;
      const newPos = this._columnWidth * i + gutterOffset;
      columnPositions.push(newPos);
    }
    this._columnPositions = columnPositions;
  }

  _getAllGridItems(gridEl) {
    const allGridItems = gridEl.querySelectorAll(
      this._config.itemClass,
    );
    return allGridItems;
  }

  _batchGridItems(allGridItemsArray, batchCallback) {
    let index = 0;
    while (allGridItemsArray.length) {
      const gridItemBatch = allGridItemsArray.splice(
        0,
        this._config.columns,
      );
      
      batchCallback(gridItemBatch, index);

      index += 1;
    }
  }

  _processRow(gridItemBatch, index) {
    
    // - - order grid item batch conditionally
    let order = this._setupOrder();
    if (this._config.equalizeColumnHeights && (index !== 0 && this._config.skipFirstRow)) {
      order = this._returnSortedOrder(gridItemBatch);
    }

    for (let i = 0; i < gridItemBatch.length; i++) {
      const orderNum = order[i];
      const gridItem = gridItemBatch[orderNum];

      const [
        width,
        height,
        aspectRatio,
        calculatedHeight,
      ] = this._returnGridItemDimensions(gridItem);

      // - - assign grid item width
      gridItem.style.width = `${this._columnWidth}px`;

      // - - assign grid item height
      gridItem.style.height = `${calculatedHeight}px`;

      // - - assign grid item x position
      gridItem.style.left = `${this._columnPositions[i]}px`;

      // - - assign item y position
      gridItem.style.top = `${this._columnHeights[i]}px`;

      // - - update column height
      this._columnHeights[i] +=
        calculatedHeight + this._config.verticalGutter;
    }
  }

  _setupOrder() {
    const order = [];
    for (let i = 0; i < this._config.columns; i++) {
      order[i] = i;
    }

    return order;
  }

  _returnSortedOrder(gridItemBatch) {
    let order = this._setupOrder();

    if (gridItemBatch.length < this._config.columns) return order;

    // - - - get order of columns by height asc
    const sortedColumnHeightIndexes = this._returnSortedIndexes([
      ...this._columnHeights,
    ]);

    // - - - get order of items by height desc
    const sortedGridItemBatchIndexes = this._returnSortedGridItemsIndexes(
      [...gridItemBatch],
    );

    // - - - match tallest item with shortest column
    for (let i = 0; i < gridItemBatch.length; i++) {
      order[sortedColumnHeightIndexes[i]] =
        sortedGridItemBatchIndexes[i];
    }
    return order;
  }

  _returnSortedIndexes(array) {
    const indexes = [];

    for (let i = 0; i < array.length; i++) {
      array[i] = [array[i], i];
    }
    array.sort(function(a, b) {
      return a[0] > b[0] ? -1 : 1;
    });

    for (var j = 0; j < array.length; j++) {
      indexes.push(array[j][1]);
    }

    return indexes;
  }

  _returnSortedGridItemsIndexes(gridItemBatch) {
    const indexes = [];

    for (let i = 0; i < gridItemBatch.length; i++) {
      const [
        width,
        height,
        aspectRatio,
        calculatedHeight,
      ] = this._returnGridItemDimensions(gridItemBatch[i]);
      gridItemBatch[i] = [calculatedHeight, i];
    }
    gridItemBatch.sort(function(a, b) {
      return a[0] < b[0] ? -1 : 1;
    });

    for (var j = 0; j < gridItemBatch.length; j++) {
      indexes.push(gridItemBatch[j][1]);
    }

    return indexes;
  }

  _returnGridItemDimensions(gridItem) {
    // - - get grid item aspect ratio
    const height = parseInt(
      gridItem.dataset[this._config.heightAttribute],
      10,
    );
    const width = parseInt(
      gridItem.dataset[this._config.widthAttribute],
      10,
    );
    const aspectRatio = height / width;
    const calculatedHeight = this._columnWidth * aspectRatio;
    return [width, height, aspectRatio, calculatedHeight];
  }

  _sortDescending(a, b) {
    return b - a;
  }
}

/* eslint-enable */
